/**
 * In-portal notification feed.
 *
 * Rows are produced by database triggers (ticket status change, staff reply,
 * document published) and by Trigger.dev tasks. The portal only ever marks a
 * row read — the guard trigger on the table rejects anything else.
 *
 * Realtime: the feed subscribes to the notifications table for the accounts in
 * scope. When the channel is not SUBSCRIBED the surface must say so rather than
 * look live, so `freshness` reports 'offline' until the channel is up.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FreshnessState } from '@pazportal/ui';
import { supabase } from '../lib/supabase';
import { useCustomerScope } from '../contexts/AccountContext';
import { useViewAs } from '../contexts/ViewAsContext';

export const NOTIFICATION_COLUMNS =
  'id, customer_id, user_id, location_id, kind, severity, title, body, href, read_at, created_at';

/** Newest first, and this many at a time — the bell is a feed, not an archive. */
export const NOTIFICATION_PAGE_SIZE = 30;

export type NotificationSeverity = 'info' | 'success' | 'warning' | 'critical';

export interface NotificationRecord {
  id: string;
  customer_id: string;
  user_id: string | null;
  location_id: string | null;
  kind: string;
  severity: NotificationSeverity;
  title: string;
  body: string | null;
  href: string | null;
  read_at: string | null;
  created_at: string;
}

function isSeverity(value: unknown): value is NotificationSeverity {
  return value === 'info' || value === 'success' || value === 'warning' || value === 'critical';
}

export interface NotificationsResult {
  notifications: NotificationRecord[];
  unreadCount: number;
  isLoading: boolean;
  isError: boolean;
  /** Live only while the realtime channel is actually subscribed. */
  freshness: FreshnessState;
  /** Last realtime event, or the last successful fetch when none has arrived. */
  lastEventAt: string | null;
  readOnly: boolean;
  markRead: (id: string) => void;
  markAllRead: () => void;
}

export function useNotifications(): NotificationsResult {
  const scope = useCustomerScope();
  const { isStaff, viewAsUserId } = useViewAs();
  // Staff viewing as a customer are read-only everywhere else in the portal;
  // marking someone else's notification read would be a write in their name.
  const readOnly = isStaff && !!viewAsUserId;
  const queryClient = useQueryClient();
  const [channelUp, setChannelUp] = useState(false);
  const [lastEventAt, setLastEventAt] = useState<string | null>(null);
  const scopeKey = scope.key;
  const latestScopeKey = useRef(scopeKey);
  latestScopeKey.current = scopeKey;

  // users.id for this session. Realtime hands every subscriber on the account
  // the events RLS lets them read, and a staff session may read more than the
  // person they are viewing as, so the addressee is checked here too.
  const myUserId = useQuery({
    queryKey: ['my-user-id'],
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase.rpc('get_my_user_id');
      if (error) throw error;
      return (data as string | null) ?? null;
    },
    staleTime: Number.POSITIVE_INFINITY,
  }).data ?? null;
  const addresseeId = viewAsUserId ?? myUserId;
  const latestAddressee = useRef(addresseeId);
  latestAddressee.current = addresseeId;

  const query = useQuery({
    queryKey: ['notifications', scopeKey],
    enabled: scope.ready && scope.ids.length > 0,
    queryFn: async (): Promise<NotificationRecord[]> => {
      const { data, error } = await supabase
        .from('notifications_customer_view')
        .select(NOTIFICATION_COLUMNS)
        .in('customer_id', scope.ids)
        .order('created_at', { ascending: false })
        .limit(NOTIFICATION_PAGE_SIZE);
      if (error) throw error;
      return (data ?? []).flatMap((row) => {
        if (!row.id || !row.customer_id || !row.title || !row.created_at) return [];
        return [{
          id: row.id,
          customer_id: row.customer_id,
          user_id: row.user_id ?? null,
          location_id: row.location_id ?? null,
          kind: row.kind ?? 'notice',
          severity: isSeverity(row.severity) ? row.severity : 'info',
          title: row.title,
          body: row.body ?? null,
          href: row.href ?? null,
          read_at: row.read_at ?? null,
          created_at: row.created_at,
        }];
      });
    },
    staleTime: 30 * 1000,
  });

  // A staff session bypasses the per-user clause in RLS, so in view-as mode the
  // list is narrowed here to exactly what the viewed user would see.
  const notifications = useMemo(() => {
    const rows = query.data ?? [];
    if (!viewAsUserId) return rows;
    return rows.filter((row) => row.user_id === null || row.user_id === viewAsUserId);
  }, [query.data, viewAsUserId]);

  useEffect(() => {
    if (!scope.ready || scope.ids.length === 0) {
      setChannelUp(false);
      return;
    }
    const channel = supabase
      .channel(`notifications:${scopeKey}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notifications' },
        (payload) => {
          const row = (payload.new ?? payload.old) as
            | { customer_id?: string; user_id?: string | null }
            | null;
          // The subscription is account-wide; drop anything outside scope.
          if (row?.customer_id && !scope.ids.includes(row.customer_id)) return;
          // And drop anything addressed to somebody else. RLS already stops the
          // row itself from being read, but reacting to the event would still
          // tell this user when a colleague was notified.
          const addressee = latestAddressee.current;
          if (row && row.user_id != null && addressee && row.user_id !== addressee) return;
          setLastEventAt(new Date().toISOString());
          queryClient.invalidateQueries({ queryKey: ['notifications', latestScopeKey.current] });
        },
      )
      .subscribe((status) => {
        setChannelUp(status === 'SUBSCRIBED');
      });

    return () => {
      setChannelUp(false);
      void supabase.removeChannel(channel);
    };
    // scope.ids is covered by scopeKey, which is its stable serialisation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, scope.ready, scopeKey]);

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      if (readOnly) return;
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', id)
        .is('read_at', null);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications', scopeKey] }),
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      if (readOnly) return;
      const unread = notifications.filter((row) => !row.read_at).map((row) => row.id);
      if (unread.length === 0) return;
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .in('id', unread);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications', scopeKey] }),
  });

  // With no realtime event yet, the honest anchor is the last successful read —
  // reporting "never" next to a live chip would read as a dead feed.
  const lastFetchedAt = query.dataUpdatedAt ? new Date(query.dataUpdatedAt).toISOString() : null;

  return {
    notifications,
    unreadCount: notifications.filter((row) => !row.read_at).length,
    isLoading: query.isLoading,
    isError: query.isError,
    // Invariant 8: a feed that is not streaming must never read as live.
    freshness: channelUp ? 'live' : 'offline',
    lastEventAt: lastEventAt ?? lastFetchedAt,
    readOnly,
    markRead: (id: string) => markRead.mutate(id),
    markAllRead: () => markAllRead.mutate(),
  };
}
